// ---------------------------------------------------------------------------
// Tools & calibration register (Projects → Tools): test equipment, its calibrations, who holds it and where.
// ---------------------------------------------------------------------------
import { z } from 'zod';

export const TOOL_CATEGORIES = [
  { value: 'certifier', label: 'Cable certifier (e.g. Fluke DSX)' }, { value: 'tester', label: 'Tester' }, { value: 'otdr', label: 'OTDR / fiber tester' },
  { value: 'meter', label: 'Meter (multimeter, power meter…)' }, { value: 'splicer', label: 'Fusion splicer' }, { value: 'power_tool', label: 'Power tool' },
  { value: 'other', label: 'Other' },
] as const;
export const TOOL_STATUSES = [
  { value: 'available', label: 'Available (in store)' }, { value: 'in_use', label: 'In use' }, { value: 'out_for_calibration', label: 'Out for calibration' },
  { value: 'repair', label: 'In repair' }, { value: 'lost', label: 'Lost' }, { value: 'retired', label: 'Retired' },
] as const;
export const CALIBRATION_RESULTS = [{ value: 'pass', label: 'Pass' }, { value: 'adjusted', label: 'Pass after adjustment' }, { value: 'fail', label: 'Fail' }] as const;

export type CalibrationState = 'not_required' | 'valid' | 'due_soon' | 'expired' | 'unknown';
export const CALIBRATION_STATE_LABEL: Record<CalibrationState, string> = {
  not_required: 'No calibration needed', valid: 'Calibrated', due_soon: 'Calibration due soon', expired: 'Calibration expired', unknown: 'No calibration recorded',
};
/** Where a tool stands: expired after its due date, due soon within `warnDays`. */
export function calibrationState(t: { calibrationRequired: boolean; calibrationDue: string | null }, today: string, warnDays = 30): CalibrationState {
  if (!t.calibrationRequired) return 'not_required';
  if (!t.calibrationDue) return 'unknown';
  if (t.calibrationDue < today) return 'expired';
  const days = Math.round((Date.parse(`${t.calibrationDue}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400_000);
  return days <= warnDays ? 'due_soon' : 'valid';
}
/** The next due date: the calibration date plus the tool's interval. */
export function nextDue(calibratedOn: string, months: number): string {
  const d = new Date(`${calibratedOn}T00:00:00Z`);
  const day = d.getUTCDate();
  d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + months);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d.toISOString().slice(0, 10);
}

const date = z.preprocess((v) => (v === '' ? null : v), z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD').nullable()).optional();
const text = (max: number) => z.preprocess((v) => (v === '' ? null : v), z.string().trim().max(max).nullable()).optional();
const uuid = z.preprocess((v) => (v === '' ? null : v), z.string().uuid().nullable()).optional();

export const toolSchema = z.object({
  name: z.string().trim().min(1, 'Name the tool').max(200),
  category: z.enum(['certifier', 'tester', 'otdr', 'meter', 'splicer', 'power_tool', 'other']).optional(),
  brand: text(80), model: text(120), serialNo: text(120), purchaseDate: date, supplier: text(200),
  value: z.preprocess((v) => (v === '' ? null : v), z.number().min(0).max(1e12).nullable()).optional(),
  status: z.enum(['available', 'in_use', 'out_for_calibration', 'repair', 'lost', 'retired']).optional(),
  location: text(200),
  calibrationRequired: z.boolean().optional(),
  intervalMonths: z.number().int().min(1).max(120).optional(),
  /** When the tool is added: its current calibration (from the last certificate). */
  lastCalibrated: date,
  calibrationDue: date,
  notes: text(4000),
}).strict();
export const toolUpdateSchema = toolSchema.partial().strict();
export const calibrationSchema = z.object({
  calibratedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Give the calibration date'),
  /** Empty: calibration date + the tool's interval. */
  dueOn: date,
  lab: text(200), certificateNo: text(120),
  certificateUrl: z.preprocess((v) => (v === '' ? null : v), z.string().trim().url('Paste the full link (https://…)').max(1000).refine((u) => /^https?:\/\//i.test(u), 'Paste a web link (https://…)').nullable()).optional(),
  result: z.enum(['pass', 'adjusted', 'fail']).optional(),
  cost: z.preprocess((v) => (v === '' ? null : v), z.number().min(0).max(1e9).nullable()).optional(),
  notes: text(2000),
}).strict();
export const toolHandoverSchema = z.object({
  holderId: uuid, projectId: uuid, location: text(200), note: text(1000),
  status: z.enum(['available', 'in_use', 'out_for_calibration', 'repair', 'lost', 'retired']).optional(),
}).strict();

export interface ToolRow {
  id: string; tag: string; name: string; category: string; brand: string | null; model: string | null; serialNo: string | null;
  status: string; holderId: string | null; holderName: string | null; projectId: string | null; projectNumber: string | null; projectName: string | null;
  location: string | null; calibrationRequired: boolean; intervalMonths: number; lastCalibrated: string | null; calibrationDue: string | null;
  state: CalibrationState; daysToDue: number | null;
}
export interface ToolDetail extends ToolRow {
  purchaseDate: string | null; supplier: string | null; value: number | null; notes: string | null; createdAt: string;
  calibrations: { id: string; calibratedOn: string; dueOn: string; lab: string | null; certificateNo: string | null; certificateUrl: string | null; result: string; cost: number | null; notes: string | null; recordedByName: string | null }[];
  assignments: { id: string; holderName: string | null; projectNumber: string | null; projectName: string | null; location: string | null; fromAt: string; toAt: string | null; note: string | null; recordedByName: string | null }[];
}
export interface ToolsList {
  tools: ToolRow[]; canManage: boolean;
  counts: Record<CalibrationState, number>;
  people: { id: string; name: string }[]; projects: { id: string; number: string; name: string }[];
}
